import React, { useContext } from "react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem.tsx";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import { LanguageNameSpaces, SignalProcessedGeneralData } from "@shared/types";
import Title from "common/Typography/Title.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import Row from "antd/es/row";
import Col from "antd/es/col";
import styled, { useTheme } from "styled-components";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import TradeProgressGraph from "features/trades/common/TradeProgressGraph.tsx";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import NewLookSignalGeneral from "features/signals/NewLook/NewLookSignalGeneral.tsx";

const GeneralInfo = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { generalDataQuery } = useContext(SignalDetailsContext)!;
  const { isNewLook } = useTheme();

  if (isNewLook) return <NewLookSignalGeneral />;

  const getSectionData = (sectionKeys: string[]) => {
    return Object.entries(generalDataQuery.data?.general || {})
      .filter(([key]) => sectionKeys.includes(key))
      .sort((a, b) => sectionKeys.indexOf(a[0]) - sectionKeys.indexOf(b[0]));
  };

  const getParsedValue = (key: string, value: string | number) => {
    if (key === "Exchanges") {
      const exchangesArray = value.toString().split(", ");
      return (
        <ExchangesIconsGroup
          exchanges={exchangesArray}
          size={24}
          style={{ marginLeft: 8, marginTop: 4 }}
        />
      );
    }
    return value;
  };

  const getDisplayedData = (section: GeneralTabSections) => {
    return (
      <div>
        <Title
          level={2}
          $weight={typography.fontWeights.SEMI_BOLD}
          style={{ marginBottom: 16 }}
        >
          {t(section)}
        </Title>
        <Row gutter={[16, 16]}>
          {getSectionData(GENERAL_TAB_SECTION_TO_KEYS[section]).map(
            ([name, value]) => (
              <Col span={12} key={name}>
                <ExtraInfoItem
                  key={name}
                  name={t(FIELD_NAME_TO_TRANSLATION_KEY[name])}
                  value={getParsedValue(name, value)}
                  style={{ flexDirection: "column", alignItems: "flex-start" }}
                  ellipsis={true}
                />
              </Col>
            )
          )}
        </Row>
      </div>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={!generalDataQuery.currentData}
      hideContentWhileLoading={true}
      spinnerSize="large"
    >
      <TradeProgressGraph
        graphData={generalDataQuery?.data?.graph_data}
        size="l"
        style={{ marginBottom: 24 }}
      />
      {getDisplayedData(GeneralTabSections.GROUP_EXCHANGES)}
      <Divider />
      {getDisplayedData(GeneralTabSections.SIGNAL_DETAILS)}
    </LoadingSpinnerOverlay>
  );
};

export default GeneralInfo;

enum GeneralTabSections {
  GROUP_EXCHANGES = "general_info.group_and_exchanges",
  SIGNAL_DETAILS = "general_info.signal_details",
}

const GENERAL_TAB_SECTION_TO_KEYS: Record<
  GeneralTabSections,
  (keyof SignalProcessedGeneralData)[]
> = {
  [GeneralTabSections.GROUP_EXCHANGES]: ["Group", "Exchanges", "Published by"],
  [GeneralTabSections.SIGNAL_DETAILS]: [
    "Opened at",
    "Long/Short",
    "Leverage",
    "Regular/Breakout",
    "Number of open trades",
    "Number of trades",
    "Open Trades Volume",
  ],
};

const FIELD_NAME_TO_TRANSLATION_KEY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  Group: "general_info.group",
  Exchanges: "general_info.exchanges",
  "Published by": "general_info.published_by",
  "Opened at": "general_info.opened_at",
  "Long/Short": "general_info.long_short",
  Leverage: "general_info.leverage",
  "Regular/Breakout": "general_info.regular_breakout",
  "Number of open trades": "general_info.number_of_open_trades",
  "Number of trades": "general_info.number_of_trades",
  "Open Trades Volume": "general_info.open_trades_volume",
};

const Divider = styled.div`
  border-top: ${(p) => p.theme.borders.lightest};
  margin-top: 20px;
  padding-bottom: 20px;
`;
